"use client";

import { useMemo, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";
import { MessageCircle } from "lucide-react";
import { useTheme } from "@/components/providers/theme-provider";
import type { PublicWorker } from "@/constants/workers";
import { teams, workers as allWorkers } from "@/constants/workers";

interface WorkerResultsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    query: string;
    workers: PublicWorker[];
}

const POLY_URL = "https://poly.agencyofpoly.com/";
const ASK_POLY_URL = `${POLY_URL}?action=ask`;

// Nav tabs for the mini-app — Teams added between Workers and Pipelines
const NAV_TABS = ["Workers", "Teams", "Pipelines", "Workflows", "Analytics", "Settings"] as const;
type NavTab = (typeof NAV_TABS)[number];

// Category filters inside the Workers tab
const WORKER_CATEGORIES = [
    { label: "All", icon: "🏠", key: "All" },
    { label: "Sales", icon: "📈", key: "Sales & Outreach" },
    { label: "Content", icon: "✍️", key: "Content & Creative" },
    { label: "Operations", icon: "⚙️", key: "Operations & Admin" },
    { label: "Research", icon: "🔍", key: "Research & Data" },
    { label: "Support", icon: "💬", key: "Customer & Support" },
    { label: "Engineering", icon: "🛠️", key: "Engineering & DevOps" },
    { label: "Strategy", icon: "🎯", key: "Strategy & Planning" },
    { label: "Automation", icon: "🤖", key: "Research & Automation" },
];

// Mock data for Pipelines tab
const MOCK_PIPELINES = [
    { name: "Sales Pipeline", stages: ["Prospect", "Qualify", "Pitch", "Negotiate", "Close"], status: "Active", workers: 3 },
    { name: "Content Pipeline", stages: ["Ideate", "Draft", "Edit", "Publish", "Promote"], status: "Active", workers: 4 },
    { name: "Support Pipeline", stages: ["Ticket", "Triage", "Resolve", "Follow-up"], status: "Active", workers: 2 },
    { name: "Onboarding Flow", stages: ["Welcome", "Setup", "Train", "Activate"], status: "Draft", workers: 1 },
];

// Mock data for Workflows tab
const MOCK_WORKFLOWS = [
    { name: "Lead Qualification Flow", trigger: "New DM received", actions: 4, status: "Running" },
    { name: "Content Calendar Automation", trigger: "Weekly schedule", actions: 6, status: "Running" },
    { name: "Invoice Processing", trigger: "New invoice uploaded", actions: 3, status: "Paused" },
    { name: "Email Follow-up Sequence", trigger: "Lead not responded 48h", actions: 4, status: "Draft" },
];

// Mock data for Analytics tab
const MOCK_METRICS = [
    { label: "Active Workers", value: "12", change: "+3 this week", up: true },
    { label: "Tasks Completed", value: "847", change: "+124 today", up: true },
    { label: "Revenue Impact", value: "$24.5k", change: "+18% MoM", up: true },
    { label: "Avg Response Time", value: "2.3s", change: "-0.5s faster", up: true },
];

// Unlock CTA shared by locked tabs
function TabLockCTA({ title, description }: { title: string; description: string }) {
    return (
        <div className="relative">
            <div className="absolute -top-12 inset-x-0 h-12 bg-gradient-to-t from-card to-transparent pointer-events-none" />
            <div className="px-4 sm:px-6 py-8 text-center bg-card">
                <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1.5">
                    {title}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
                    {description}
                </p>
                <a
                    href={POLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground text-background text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                >
                    Get Started
                </a>
            </div>
        </div>
    );
}

export function WorkerResultsPanel({ isOpen, onClose, query, workers }: WorkerResultsPanelProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState<NavTab>("Workers");
    const [activeCategory, setActiveCategory] = useState("All");
    const { theme } = useTheme();
    const effectiveTheme =
        theme === "system"
            ? (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
            : theme;

    // Reset state when panel opens — map incoming query to a team or category
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
            const q = query.toLowerCase().trim();
            // If query strongly matches a team name, open the Teams tab
            const matchedTeam = q
                ? teams.find(
                    (t) =>
                        t.name.toLowerCase().includes(q) ||
                        q.includes("team") ||
                        t.specializations.some((s) => s.toLowerCase().includes(q))
                )
                : null;
            setActiveTab(matchedTeam ? "Teams" : "Workers");
            const matchedCat = WORKER_CATEGORIES.find(
                (c) => c.key !== "All" && query.toLowerCase().includes(c.key.toLowerCase())
            );
            setActiveCategory(matchedCat?.key || "All");
            // Scroll content to top
            if (scrollRef.current) scrollRef.current.scrollTop = 0;
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen, query]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [isOpen, onClose]);

    // Filter workers by active category + optional text query
    const filteredWorkers = useMemo(() => {
        let result = workers;
        if (activeCategory !== "All") {
            result = result.filter((w) => w.category === activeCategory);
        }
        // Apply text query only if it's not a category name
        const isCategoryQuery = WORKER_CATEGORIES.some(
            (c) => c.key.toLowerCase() === query.toLowerCase()
        );
        if (query && !isCategoryQuery) {
            const q = query.toLowerCase();
            result = result.filter(
                (w) =>
                    w.name.toLowerCase().includes(q) ||
                    w.role.toLowerCase().includes(q) ||
                    w.department.toLowerCase().includes(q)
            );
        }
        return result;
    }, [query, workers, activeCategory]);

    // Scroll to top when tab or category changes
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [activeTab, activeCategory]);

    const totalCount = filteredWorkers.length;

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[200]">
                    {/* Backdrop */}
                    <motion.div
                        className="absolute inset-0 bg-black/60"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={onClose}
                        aria-hidden="true"
                    />

                    {/* Floating card container — centered with margins on sm+ */}
                    <div className="absolute inset-0 flex items-end justify-center sm:p-6 lg:p-8 pointer-events-none">
                        <motion.div
                            className="pointer-events-auto w-full sm:max-w-5xl flex flex-col rounded-t-2xl sm:rounded-2xl shadow-2xl border border-border bg-card overflow-hidden"
                            style={{ maxHeight: "min(85vh, 720px)" }}
                            initial={{ y: "100%", opacity: 0.8 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: "100%", opacity: 0 }}
                            transition={{ type: "spring", damping: 32, stiffness: 320 }}
                        >
                            {/* ── App chrome header ── */}
                            <div className="flex items-center justify-between px-3 sm:px-5 py-2 border-b border-border bg-card">
                                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                                    {/* Logo — theme-aware Poly186 wordmark */}
                                    <a
                                        href={POLY_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center shrink-0"
                                    >
                                        <img
                                            src={effectiveTheme === "dark" ? "/poly186-logo-white.png" : "/poly186-logo-purple.png"}
                                            alt="Poly186"
                                            className="h-5 sm:h-6 w-auto object-contain"
                                        />
                                    </a>

                                    {/* Nav tabs — scrollable on mobile */}
                                    <nav className="flex items-center gap-0.5 overflow-x-auto scrollbar-none text-xs">
                                        {NAV_TABS.map((tab) => (
                                            <button
                                                key={tab}
                                                onClick={() => setActiveTab(tab)}
                                                className={`px-2 sm:px-2.5 py-1.5 rounded-md transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab
                                                    ? "text-foreground font-medium bg-accent"
                                                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                                                    }`}
                                            >
                                                {tab}
                                            </button>
                                        ))}
                                    </nav>
                                </div>
                                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                                    {/* Search input — visible on md+ */}
                                    <div className="hidden md:flex items-center gap-2 rounded-lg bg-accent border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-primary transition-colors cursor-pointer" onClick={() => window.open(POLY_URL, '_blank')}>
                                        <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <circle cx="11" cy="11" r="8" />
                                            <path d="m21 21-4.35-4.35" />
                                        </svg>
                                        <span className="w-20 text-muted-foreground/60">Search...</span>
                                    </div>
                                    {/* User avatar */}
                                    <img
                                        src="https://i.pravatar.cc/80?img=12"
                                        alt="User"
                                        className="h-7 w-7 rounded-full object-cover border border-border/50 cursor-pointer"
                                        onClick={() => window.open(POLY_URL, '_blank')}
                                    />
                                    <button
                                        onClick={onClose}
                                        className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                                        aria-label="Close panel"
                                    >
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* ── Tab content ── */}
                            <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain">
                                {activeTab === "Workers" && (
                                    <WorkersTabContent
                                        workers={filteredWorkers}
                                        totalCount={totalCount}
                                        query={query}
                                        activeCategory={activeCategory}
                                        onCategoryChange={setActiveCategory}
                                    />
                                )}
                                {activeTab === "Teams" && <TeamsTabContent query={query} onClose={onClose} />}
                                {activeTab === "Pipelines" && <PipelinesTabContent />}
                                {activeTab === "Workflows" && <WorkflowsTabContent />}
                                {activeTab === "Analytics" && <AnalyticsTabContent />}
                                {activeTab === "Settings" && <SettingsTabContent />}
                            </div>

                            {/* ── Ask Poly footer — persistent across all tabs ── */}
                            <AskPolyFooter query={query} />
                        </motion.div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
}

/* ═══════════════════════════════════════════════════════
   Workers Tab — interactive category filters + table
   ═══════════════════════════════════════════════════════ */

function WorkersTabContent({
    workers,
    totalCount,
    query,
    activeCategory,
    onCategoryChange,
}: {
    workers: PublicWorker[];
    totalCount: number;
    query: string;
    activeCategory: string;
    onCategoryChange: (cat: string) => void;
}) {
    return (
        <div>
            {/* Category pills + count bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 sm:px-5 py-2.5 border-b border-border/50">
                <div className="flex items-center gap-1 sm:gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
                    {WORKER_CATEGORIES.map((cat) => (
                        <button
                            key={cat.key}
                            onClick={() => onCategoryChange(cat.key)}
                            className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-all duration-150 cursor-pointer border ${activeCategory === cat.key
                                ? "bg-foreground text-background border-foreground font-medium shadow-sm"
                                : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary hover:bg-accent/50"
                                }`}
                        >
                            <span className="text-[11px]">{cat.icon}</span>
                            <span>{cat.label}</span>
                        </button>
                    ))}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                    <span className="font-semibold text-foreground">{totalCount}</span> {totalCount === 1 ? "worker" : "workers"}
                    {query && activeCategory === "All" && (
                        <span> for &ldquo;{query}&rdquo;</span>
                    )}
                </span>
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-card z-10">
                        <tr className="border-b border-border/50 text-left">
                            <th className="pl-5 pr-2 py-2.5 w-8">
                                <div className="h-4 w-4 rounded border border-primary" />
                            </th>
                            <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                            <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">Position</th>
                            <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Availability</th>
                            <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">Department</th>
                            <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {workers.slice(0, 6).map((worker, idx) => {
                            const isLocked = idx > 0;
                            return (
                                <tr
                                    key={worker.name}
                                    className={`border-b border-border/50 transition-colors ${isLocked ? "select-none" : "hover:bg-accent/50 cursor-pointer"}`}
                                    style={isLocked ? { filter: `blur(${2 + idx}px)`, opacity: 0.6 } : undefined}
                                >
                                    <td className="pl-5 pr-2 py-2.5">
                                        <div className="h-4 w-4 rounded border border-border" />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={worker.avatar}
                                                alt={worker.name}
                                                loading="lazy"
                                                decoding="async"
                                                className="h-8 w-8 rounded-full object-cover border border-border/50 shrink-0"
                                            />
                                            <span className="font-medium text-foreground whitespace-nowrap">{worker.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5 text-foreground hidden md:table-cell max-w-xs truncate">{worker.role}</td>
                                    <td className="px-3 py-2.5 text-muted-foreground hidden lg:table-cell whitespace-nowrap">{worker.availability}</td>
                                    <td className="px-3 py-2.5">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-accent text-muted-foreground border border-border/50">
                                            {worker.department}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        {isLocked ? (
                                            <span className="px-3 py-1 rounded-md text-xs border border-border text-muted-foreground whitespace-nowrap">Deploy</span>
                                        ) : (
                                            <Link
                                                to={`/workers/${worker.slug}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className="px-3 py-1 rounded-md text-xs border border-border text-foreground hover:bg-accent transition-colors whitespace-nowrap"
                                            >
                                                Deploy
                                            </Link>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Mobile card list */}
            <div className="sm:hidden divide-y divide-border/50">
                {workers.slice(0, 6).map((worker, idx) => {
                    const isLocked = idx > 0;
                    return (
                        <div
                            key={worker.name}
                            className={`p-4 ${isLocked ? "select-none" : ""}`}
                            style={isLocked ? { filter: `blur(${2 + idx}px)`, opacity: 0.6 } : undefined}
                        >
                            <div className="flex items-start gap-3">
                                <img
                                    src={worker.avatar}
                                    alt={worker.name}
                                    loading="lazy"
                                    decoding="async"
                                    className="h-10 w-10 rounded-full object-cover border border-border/50 shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-medium text-sm text-foreground">{worker.name}</span>
                                        {isLocked ? (
                                            <span className="px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground shrink-0">Deploy</span>
                                        ) : (
                                            <Link
                                                to={`/workers/${worker.slug}`}
                                                className="px-3 py-1.5 rounded-lg text-xs border border-border text-foreground shrink-0"
                                            >
                                                Deploy
                                            </Link>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate mt-0.5">{worker.role}</div>
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-accent text-muted-foreground border border-border/50">
                                            {worker.department}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Unlock CTA below blurred rows */}
            <TabLockCTA
                title="Unlock All Digital Workers"
                description="Get onboarded to deploy any worker from the full catalog instantly."
            />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════
   Pipelines Tab — first visible, rest blurred
   ═══════════════════════════════════════════════════════ */

function PipelinesTabContent() {
    return (
        <div>
            <div className="px-4 sm:px-5 py-3 border-b border-border/50">
                <span className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{MOCK_PIPELINES.length}</span> active pipelines
                </span>
            </div>
            <div className="divide-y divide-border/50">
                {MOCK_PIPELINES.map((pipe, idx) => {
                    const isLocked = idx > 0;
                    return (
                        <a
                            key={pipe.name}
                            href={POLY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`block px-4 sm:px-5 py-4 transition-colors ${isLocked ? "select-none pointer-events-none" : "hover:bg-accent/50"}`}
                            style={isLocked ? { filter: `blur(${2 + idx}px)`, opacity: 0.5 } : undefined}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm text-foreground">{pipe.name}</span>
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${pipe.status === "Active" ? "bg-green-500/10 text-green-600" : "bg-accent text-muted-foreground"}`}>
                                        {pipe.status}
                                    </span>
                                    <span className="text-xs text-muted-foreground">{pipe.workers} workers</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                {pipe.stages.map((stage, sIdx) => (
                                    <span key={stage} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                        <span className="px-2 py-0.5 rounded bg-accent border border-border/50">
                                            {stage}
                                        </span>
                                        {sIdx < pipe.stages.length - 1 && (
                                            <svg className="h-3 w-3 text-muted-foreground/40 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path d="M9 5l7 7-7 7" />
                                            </svg>
                                        )}
                                    </span>
                                ))}
                            </div>
                        </a>
                    );
                })}
            </div>
            <TabLockCTA
                title="Unlock Pipelines"
                description="Build custom multi-stage pipelines with your digital workers."
            />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════
   Workflows Tab — first visible, rest blurred
   ═══════════════════════════════════════════════════════ */

function WorkflowsTabContent() {
    return (
        <div>
            <div className="px-4 sm:px-5 py-3 border-b border-border/50">
                <span className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{MOCK_WORKFLOWS.length}</span> workflows configured
                </span>
            </div>
            <div className="divide-y divide-border/50">
                {MOCK_WORKFLOWS.map((wf, idx) => {
                    const isLocked = idx > 0;
                    return (
                        <a
                            key={wf.name}
                            href={POLY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`block px-4 sm:px-5 py-4 transition-colors ${isLocked ? "select-none pointer-events-none" : "hover:bg-accent/50"}`}
                            style={isLocked ? { filter: `blur(${2 + idx}px)`, opacity: 0.5 } : undefined}
                        >
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="font-medium text-sm text-foreground">{wf.name}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${wf.status === "Running" ? "bg-green-500/10 text-green-600" :
                                    wf.status === "Paused" ? "bg-amber-500/10 text-amber-600" :
                                        "bg-accent text-muted-foreground"
                                    }`}>
                                    {wf.status}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>Trigger: {wf.trigger}</span>
                                <span>•</span>
                                <span>{wf.actions} actions</span>
                            </div>
                        </a>
                    );
                })}
            </div>
            <TabLockCTA
                title="Unlock Workflows"
                description="Automate multi-step processes with triggers, conditions, and worker actions."
            />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════
   Analytics Tab — metric cards visible, chart blurred
   ═══════════════════════════════════════════════════════ */

function AnalyticsTabContent() {
    return (
        <div>
            {/* Metric cards — visible teaser */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 sm:p-5">
                {MOCK_METRICS.map((m) => (
                    <a
                        key={m.label}
                        href={POLY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-xl border border-border p-3 hover:bg-accent/50 transition-colors"
                    >
                        <div className="text-xs text-muted-foreground mb-1">{m.label}</div>
                        <div className="text-xl font-semibold text-foreground">{m.value}</div>
                        <div className="text-xs text-green-600 mt-1">{m.change}</div>
                    </a>
                ))}
            </div>

            {/* Mock chart area — blurred */}
            <div className="px-4 sm:px-5 pb-2">
                <div
                    className="rounded-xl border border-border p-6 select-none"
                    style={{ filter: "blur(6px)", opacity: 0.4 }}
                >
                    <div className="h-4 w-32 bg-accent rounded mb-4" />
                    <div className="flex items-end gap-2 h-32">
                        {[40, 65, 45, 80, 55, 90, 70, 85, 60, 75, 95, 50].map((h, i) => (
                            <div
                                key={i}
                                className="flex-1 bg-primary/30 rounded-t"
                                style={{ height: `${h}%` }}
                            />
                        ))}
                    </div>
                    <div className="flex justify-between mt-2">
                        {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m) => (
                            <span key={m} className="text-[10px] text-muted-foreground">{m}</span>
                        ))}
                    </div>
                </div>
            </div>

            <TabLockCTA
                title="Unlock Analytics"
                description="Track worker performance, task completion rates, and revenue impact in real time."
            />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════
   Settings Tab — fully blurred
   ═══════════════════════════════════════════════════════ */

const SETTINGS_SECTIONS = [
    {
        title: "Workspace",
        icon: "🏢",
        fields: [
            { label: "Workspace Name", value: "My Agency" },
            { label: "Plan", value: "Starter" },
        ],
    },
    {
        title: "Team Members",
        icon: "👥",
        fields: [
            { label: "Active Members", value: "3 of 5" },
            { label: "Pending Invites", value: "1" },
        ],
    },
    {
        title: "Billing",
        icon: "💳",
        fields: [
            { label: "Current Plan", value: "$49/mo" },
            { label: "Next Payment", value: "Mar 1, 2026" },
        ],
    },
    {
        title: "API Keys",
        icon: "🔑",
        fields: [
            { label: "Production Key", value: "pk_live_••••••4f8a" },
            { label: "Test Key", value: "pk_test_••••••9c2d" },
        ],
    },
    {
        title: "Integrations",
        icon: "🔗",
        fields: [
            { label: "Connected", value: "Slack, HubSpot" },
            { label: "Available", value: "12 more" },
        ],
    },
];

function SettingsTabContent() {
    return (
        <div>
            {/* Nav sidebar + content layout */}
            <div className="flex flex-col sm:flex-row">
                {/* Settings nav — horizontal on mobile, vertical on sm+ */}
                <div className="sm:w-48 sm:border-r border-b sm:border-b-0 border-border/50 bg-card">
                    <div className="flex sm:flex-col overflow-x-auto sm:overflow-visible scrollbar-none">
                        {SETTINGS_SECTIONS.map((section, idx) => (
                            <a
                                key={section.title}
                                href={POLY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 px-4 py-2.5 text-xs whitespace-nowrap transition-colors ${idx === 0
                                    ? "text-foreground font-medium bg-accent/50"
                                    : "text-muted-foreground hover:text-foreground hover:bg-accent/30"
                                    }`}
                            >
                                <span>{section.icon}</span>
                                <span>{section.title}</span>
                            </a>
                        ))}
                    </div>
                </div>

                {/* Settings content */}
                <div className="flex-1">
                    {/* First section visible */}
                    <div className="px-4 sm:px-5 py-4">
                        <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                            <span>{SETTINGS_SECTIONS[0].icon}</span>
                            {SETTINGS_SECTIONS[0].title}
                        </h3>
                        <div className="space-y-3">
                            {SETTINGS_SECTIONS[0].fields.map((field) => (
                                <div key={field.label} className="flex items-center justify-between py-2 border-b border-border/50">
                                    <span className="text-xs text-muted-foreground">{field.label}</span>
                                    <span className="text-xs text-foreground font-medium">{field.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Blurred peek of next section */}
                    <div className="select-none" style={{ filter: "blur(4px)", opacity: 0.4 }}>
                        <div className="px-4 sm:px-5 py-4 border-t border-border/50">
                            <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                                <span>{SETTINGS_SECTIONS[1].icon}</span>
                                {SETTINGS_SECTIONS[1].title}
                            </h3>
                            <div className="space-y-3">
                                {SETTINGS_SECTIONS[1].fields.map((field) => (
                                    <div key={field.label} className="flex items-center justify-between py-2 border-b border-border/50">
                                        <span className="text-xs text-muted-foreground">{field.label}</span>
                                        <span className="text-xs text-foreground font-medium">{field.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* CTA right after the blurred peek — visible without scrolling */}
                    <div className="px-4 sm:px-6 py-6 text-center">
                        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1.5">
                            Unlock Settings
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
                            Configure your workspace, manage team members, and set up integrations.
                        </p>
                        <a
                            href={POLY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground text-background text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                        >
                            Get Started
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════
   Teams Tab — real digital teams from teams.json
   First 2 fully visible, rest blurred behind unlock CTA
   ═══════════════════════════════════════════════════════ */

function TeamsTabContent({ query, onClose }: { query: string; onClose: () => void }) {
    const filtered = useMemo(() => {
        if (!query.trim()) return teams;
        const q = query.toLowerCase();
        const matched = teams.filter(
            (t) =>
                t.name.toLowerCase().includes(q) ||
                t.description.toLowerCase().includes(q) ||
                t.specializations.some((s) => s.toLowerCase().includes(q))
        );
        return matched.length > 0 ? matched : teams;
    }, [query]);

    const VISIBLE_COUNT = 2;

    return (
        <div>
            <div className="px-4 sm:px-5 py-3 border-b border-border/50 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{filtered.length}</span> digital{" "}
                    {filtered.length === 1 ? "team" : "teams"}
                </span>
                <Link
                    to="/teams"
                    onClick={onClose}
                    className="text-xs text-primary hover:underline"
                >
                    Browse all →
                </Link>
            </div>

            <div className="divide-y divide-border/50">
                {filtered.slice(0, 6).map((team, idx) => {
                    const isLocked = idx >= VISIBLE_COUNT;
                    const memberAvatars = team.members
                        .slice(0, team.memberCount > 6 ? 5 : 6)
                        .map((slug) => allWorkers.find((w) => w.slug === slug))
                        .filter(Boolean) as PublicWorker[];
                    const hiddenCount = Math.max(team.memberCount - memberAvatars.length, 0);

                    const Card = (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                            {/* Avatar grid */}
                            <div className="grid w-16 grid-cols-3 gap-0.5 shrink-0">
                                {memberAvatars.map((w) => (
                                    <img
                                        key={w.slug}
                                        src={w.avatar}
                                        alt={w.name}
                                        loading="lazy"
                                        decoding="async"
                                        className="aspect-square w-full rounded object-cover"
                                    />
                                ))}
                                {hiddenCount > 0 && (
                                    <span className="flex aspect-square w-full items-center justify-center rounded bg-accent text-[10px] text-muted-foreground font-medium">
                                        +{hiddenCount}
                                    </span>
                                )}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-0.5">
                                    <span className="font-medium text-sm text-foreground truncate">
                                        {team.name}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0 whitespace-nowrap">
                                        {team.coordinationPattern.replace(/_/g, " ")}
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground line-clamp-1 mb-1.5">
                                    {team.description}
                                </p>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {team.specializations.slice(0, 3).map((spec) => (
                                        <span
                                            key={spec}
                                            className="px-1.5 py-0.5 rounded text-[10px] bg-accent border border-border/50 text-muted-foreground"
                                        >
                                            {spec.replace(/_/g, " ")}
                                        </span>
                                    ))}
                                    <span className="text-[10px] text-muted-foreground/70">
                                        {team.memberCount} workers · {team.stats.totalExecutions.toLocaleString()} exec
                                    </span>
                                </div>
                            </div>

                            <div className="shrink-0">
                                {isLocked ? (
                                    <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground">
                                        Hire
                                    </span>
                                ) : (
                                    <Link
                                        to={`/teams/${team.slug}`}
                                        onClick={onClose}
                                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs border border-border text-foreground hover:bg-accent transition-colors"
                                    >
                                        Hire team
                                    </Link>
                                )}
                            </div>
                        </div>
                    );

                    return (
                        <div
                            key={team.slug}
                            className={`px-4 sm:px-5 py-3.5 transition-colors ${isLocked ? "select-none pointer-events-none" : "hover:bg-accent/40"}`}
                            style={isLocked ? { filter: `blur(${2 + (idx - VISIBLE_COUNT)}px)`, opacity: 0.55 } : undefined}
                        >
                            {Card}
                        </div>
                    );
                })}
            </div>

            <TabLockCTA
                title="Unlock All Digital Teams"
                description="Deploy entire coordinated teams that hand off work between members automatically."
            />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════
   Ask Poly footer — persistent strip across all tabs
   ═══════════════════════════════════════════════════════ */

function AskPolyFooter({ query }: { query: string }) {
    const askUrl = query.trim().length > 0
        ? `${ASK_POLY_URL}&q=${encodeURIComponent(query.trim())}`
        : ASK_POLY_URL;

    return (
        <a
            href={askUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 px-4 sm:px-5 py-3 border-t border-border bg-card hover:bg-accent/50 transition-colors shrink-0"
        >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 border border-primary/20 shrink-0">
                <MessageCircle className="h-4 w-4 text-primary" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm text-foreground">
                    {query.trim().length > 0 ? (
                        <>Ask Poly: <span className="font-medium">&ldquo;{query.trim()}&rdquo;</span></>
                    ) : (
                        <>Ask Poly anything <span className="text-muted-foreground font-light">— describe the role you need</span></>
                    )}
                </div>
                <div className="text-[11px] text-muted-foreground hidden sm:block">
                    Poly will recommend workers, teams, or pipelines for your use case.
                </div>
            </div>
            <span className="text-xs text-primary font-medium shrink-0 group-hover:translate-x-0.5 transition-transform">
                Ask →
            </span>
        </a>
    );
}
